import {AdminApiDal} from '../adminApiDal';
import {inject, injectable} from '../../../../modules/common/di';
import {GetDevicesApiResponse} from './getDevicesApiResponse';
import {SimulatedDeviceCopyApiResponse} from "./simulatedDeviceCopyApiResponse";
import {AppApiDal} from "@peoplepower/webcore/dist/types/data/api/app/appApiDal";
import {ApiResponseBase} from '../../../models/apiResponseBase';

export enum SimCardStatus {
  Active = 1,
  Deactivated = 5,
}

/**
 * API to manage devices in Organization.
 * See {@link https://iotadmins.docs.apiary.io/#reference/devices/devices}
 */
@injectable('DevicesAdminApi')
export class DevicesAdminApi {
  @inject('AdminApiDal') protected readonly dal!: AdminApiDal;
  @inject('AppApiDal') protected readonly appDal!: AppApiDal;

  /**
   * Retrieve device instances linked to locations inside the organization.
   * See {@link https://iotadmins.docs.apiary.io/#reference/devices/devices/get-devices}
   *
   * @param {number} [organizationId] Organization ID.
   * @param [params] Request parameters.
   * @param {number} [params.userId] Filter by user ID.
   * @param {number} [params.locationId] Filter by location ID.
   * @param {number} [params.deviceId] Filter by device ID.
   * @param {number|number[]} [params.deviceType] Filter by device types. Multiple values supported.
   * @param {string} [params.searchBy] Search by device ID or description.
   * @param {string} [params.searchTag] Search by tag.
   * @param {string} [params.lessUpdateDate] Request devices, where the last update date is less than this.
   * @param {string} [params.moreUpdateDate] Request devices, where the last update date is more than this.
   * @param {string} [params.paramName] Request devices, which sent this parameter to the cloud.
   * @param {string} [params.paramValue] Requested parameter value.
   * @param {number} [params.limit] Limit the response size by this number.
   * @param {boolean} [params.getTags] Return device tags.
   *
   * @returns {Promise<GetDevicesApiResponse>}
   */
  getDevices(
    organizationId?: number,
    params?: {
      userId?: number;
      locationId?: number;
      deviceId?: string;
      deviceType?: number | number[];
      searchBy?: string;
      searchTag?: string;
      lessUpdateDate?: string;
      moreUpdateDate?: string;
      paramName?: string;
      paramValue?: string;
      limit?: number;
      getTags?: boolean;
    },
  ): Promise<GetDevicesApiResponse> {
    if (organizationId) {
      // @ts-ignore
      params.organizationId = organizationId;
    }

    return this.dal.get(`devices`, {params: params});
  }

  /**
   * Simulated Device Copy.
   * Create a device simulator - an exact copy of a real device that would receive all the same data as a real device.
   * Using this API an administrator can set a device as a source simulator and register another simulated device on a required location.
   *
   * See {@link https://iotapps.docs.apiary.io/#reference/devices/simulated-device-copies/create-device-simulated-copy}
   *
   * @param {number} [locationId] Location ID that contains the source device.
   * @param {string} [deviceId] Device ID acting as a source.
   * @param [params] Request parameters.
   * @param {number} [params.simulatedLocationId] Location ID where a simulated device will be registered. Optional.
   * @returns {Promise<SimulatedDeviceCopyApiResponse>}
   */
  simulatedDeviceCopy(
    locationId: number,
    deviceId: string,
    params?: {
      simulatedLocationId?: number;
    },
  ): Promise<SimulatedDeviceCopyApiResponse> {
    return this.appDal.post(
      `locations/${encodeURIComponent(locationId.toString())}/devices/${encodeURIComponent(deviceId.toString())}/copySimulator`,
      {},
      {
        params: params
      }
    );
  }

  /**
   * Activate Sim Card.
   * Using this API an administrator can manually activate or deactivate device SIM cards.
   * - Activate (status = 1): 0 = New, 5 = Deactivated.
   * - Deactivate (status = 5): 1 = Active, 4 = Suspended.
   *
   * See {@link https://iotapps.docs.apiary.io/#/reference/devices/sim-cards/activate-sim-card}
   *
   * @param locationId Location ID that contains the source device.
   * @param deviceId Device ID acting as a source.
   * @param params Request parameters.
   * @param {SimCardStatus} params.status New SIM card status.
   * @param {number} [params.simId] SIM card ID.
   * @returns {Promise<ApiResponseBase>}
   */
  activateSimCard(
    locationId: number,
    deviceId: string,
    params: {
      status: SimCardStatus,
      simId?: number,
    }
  ): Promise<ApiResponseBase> {
    return this.appDal.put(
      `locations/${encodeURIComponent(locationId.toString())}/devices/${encodeURIComponent(deviceId.toString())}/simCard`,
      void 0,
      {
        params: params
      }
    );
  }
}
